import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FileText,
  Receipt,
  Scale,
  ShieldCheck,
  UserCheck,
  X,
} from 'lucide-react';

interface PolitiqueCartesCreditComptesDepensesBZModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'synthese' | 'politique' | 'gouvernance' | 'suivis';

const policyPdfPath = '/documents/admin-rh/politique-simplifiee-cartes-credit-comptes-depenses-bz-v14-2026-07-30.pdf';

const tabs: { id: TabId; label: string; icon: typeof BookOpen }[] = [
  { id: 'synthese', label: 'Synthèse', icon: BookOpen },
  { id: 'politique', label: 'Politique simplifiée', icon: FileText },
  { id: 'gouvernance', label: 'Gouvernance', icon: Scale },
  { id: 'suivis', label: 'Suivis administratifs', icon: CalendarClock },
];

const principles = [
  {
    title: 'Facture obligatoire',
    text: 'Chaque dépense doit être appuyée par une facture originale ou une copie complète; une preuve de paiement seule ne suffit pas.',
    icon: Receipt,
  },
  {
    title: 'Contrôle administratif final',
    text: 'L’administration conserve le dernier mot sur l’acceptation, la correction, le refus ou l’exception liée aux dépenses.',
    icon: ShieldCheck,
  },
  {
    title: 'Responsabilité partagée',
    text: 'L’employé prépare et documente, le directeur vérifie et autorise, puis l’administration valide le traitement final.',
    icon: UserCheck,
  },
];

const ruleCards = [
  { label: 'Version en vigueur', value: 'V14', note: 'Politique simplifiée confirmée dans PRIsME.' },
  { label: 'Statut PRIsME', value: 'En vigueur', note: 'Politique confirmée dans PRIsME le 2026-08-25.' },
  { label: 'Système cible', value: 'BZDepenses', note: 'Dépôt, classement, autorisation et traitement des pièces justificatives.' },
  { label: 'Échéance mensuelle', value: 'Dernier jour · 17 h', note: 'Les comptes complets reçus après l’échéance peuvent être reportés.' },
];

const raci = [
  {
    role: 'Responsable employé',
    owner: 'Employé',
    duty: 'Préparer le compte de dépenses, déposer les factures conformes, choisir le bon type de dépense et autoriser son compte dans BZDepenses.',
  },
  {
    role: 'Approbateur',
    owner: 'Directeur',
    duty: 'Vérifier la cohérence des dépenses, les factures jointes et les codes de grand livre sélectionnés avant autorisation.',
  },
  {
    role: 'Validation finale',
    owner: 'Administration',
    duty: 'Valider la conformité des factures, des codes de grand livre et des règles applicables avant paiement ou traitement de carte.',
  },
  {
    role: 'Escalade',
    owner: 'Direction / Administration',
    duty: 'Trancher les exceptions, dépenses contestées, pièces manquantes, abus, situations récurrentes ou besoins de clarification.',
  },
];

const eligibility = [
  'Tous les employés qui utilisent une carte de crédit d’entreprise.',
  'Tous les employés qui présentent un compte de dépenses.',
  'Toutes les factures déposées dans BZDepenses ou réclamées pour remboursement.',
  'Toutes les dépenses portées à une carte BZ, même si elles sont payées directement par l’entreprise.',
];

const followUps = [
  'Déterminer si la politique doit être intégrée au cahier des politiques employé ou demeurer comme annexe distincte.',
  'Aligner les catégories et codes de grand livre BZDepenses avec l’annexe A.',
  'Décider séparément si un accusé de réception ou une signature TactiCE est requis en plus de la diffusion déjà prouvée.',
];

const diffusionProof = [
  'Confirmation vocale Louis-Alexandre Hébert reçue le 2026-08-25 à 15:41 EDT.',
  'Diffusion par courriel au groupe GBZ de 58 employés, avec la politique télétravail BZ.',
  'Copies .eml conservées: copie d’envoi, copie reçue, Message-ID Outlook et pièces jointes vérifiées.',
  'État PRIsME: diffusion officielle documentée et preuve complète.',
];

function SyntheseTab() {
  return (
    <div className="space-y-6">
      <section className="grid gap-5 md:grid-cols-3">
        {principles.map((principle) => {
          const Icon = principle.icon;
          return (
            <div key={principle.title} className="rounded-2xl border border-amber-300/25 bg-amber-400/10 p-5">
              <Icon className="mb-3 h-8 w-8 text-amber-200" />
              <h3 className="text-xl font-bold">{principle.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{principle.text}</p>
            </div>
          );
        })}
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
        <div className="mb-5 flex items-center gap-3">
          <CreditCard className="h-7 w-7 text-amber-200" />
          <h3 className="text-2xl font-bold">Cadre de base</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {ruleCards.map((item) => (
            <div key={item.label} className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{item.label}</p>
              <p className="mt-2 text-xl font-black text-white">{item.value}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-300">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-emerald-300/25 bg-emerald-500/10 p-6">
          <div className="mb-4 flex items-center gap-3">
            <CheckCircle2 className="h-7 w-7 text-emerald-200" />
            <h3 className="text-2xl font-bold">Champ d’application</h3>
          </div>
          <ul className="space-y-3 text-sm leading-relaxed text-emerald-50/90">
            {eligibility.map((item) => (
              <li key={item} className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-200" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-3xl border border-rose-300/25 bg-rose-500/10 p-6">
          <div className="mb-4 flex items-center gap-3">
            <AlertTriangle className="h-7 w-7 text-rose-200" />
            <h3 className="text-2xl font-bold">Limites à garder claires</h3>
          </div>
          <ul className="space-y-3 text-sm leading-relaxed text-rose-50/90">
            <li>Une dépense non raisonnable, incomplète ou sans lien clair avec le travail peut être refusée.</li>
            <li>Une carte de crédit d’entreprise est un privilège accordé par BZ, pas un droit acquis.</li>
            <li>Les balises de l’annexe A ne sont pas des maximums automatiques ni une autorisation de dépense.</li>
            <li>Les exceptions doivent rester documentées et sujettes à validation finale.</li>
          </ul>
        </div>
      </section>

      <section className="rounded-3xl border border-emerald-300/25 bg-emerald-500/10 p-6">
        <div className="mb-4 flex items-center gap-3">
          <CheckCircle2 className="h-7 w-7 text-emerald-200" />
          <h3 className="text-2xl font-bold">Diffusion officielle documentée</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {diffusionProof.map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-emerald-200/20 bg-slate-900/65 p-5 text-sm leading-relaxed text-emerald-50/90">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-200" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function PolitiqueTab() {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
      <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-400/10 px-4 py-2 text-sm font-semibold text-amber-100">
            <FileText className="h-4 w-4" />
            Version de référence V14 · Juillet 2026
          </div>
          <h3 className="text-2xl font-bold">Politique simplifiée — cartes de crédit et comptes de dépenses</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300">
            Document de travail intégré à cette card PRIsME. Il encadre l’utilisation des cartes de crédit d’entreprise,
            la préparation des comptes de dépenses, les factures obligatoires, les codes de grand livre et l’annexe A.
          </p>
        </div>
        <a
          href={policyPdfPath}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-300 px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-amber-200"
        >
          <FileText className="h-4 w-4" />
          Ouvrir le PDF
        </a>
      </div>
      <div className="h-[72vh] overflow-hidden rounded-2xl border border-slate-700 bg-slate-900">
        <iframe title="Politique simplifiée cartes de crédit et comptes de dépenses BZ" src={policyPdfPath} className="h-full w-full" />
      </div>
    </section>
  );
}

function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-violet-300/25 bg-violet-500/10 p-6">
        <div className="mb-5 flex items-center gap-3">
          <UserCheck className="h-7 w-7 text-violet-200" />
          <h3 className="text-2xl font-bold">RACI de gouvernance</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {raci.map((item) => (
            <div key={item.role} className="rounded-2xl border border-violet-200/20 bg-slate-900/65 p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-violet-200">{item.role}</p>
              <h4 className="mt-2 text-lg font-bold">{item.owner}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.duty}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-600 bg-slate-900/80 p-6">
        <div className="mb-5 flex items-center gap-3">
          <Scale className="h-7 w-7 text-slate-200" />
          <h3 className="text-2xl font-bold">Positionnement PRIsME</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-sm font-semibold text-amber-100">Domaine</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">Administration & RH · Gouvernance</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-sm font-semibold text-amber-100">Nature</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">Politique interne BZ en vigueur dans PRIsME.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-sm font-semibold text-amber-100">Lien TactiCE</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">Diffusion officielle déjà documentée dans PRIsME; accusé de réception ou signature TactiCE à décider séparément.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function SuivisTab() {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
      <div className="mb-5 flex items-center gap-3">
        <CalendarClock className="h-7 w-7 text-amber-200" />
        <h3 className="text-2xl font-bold">Suivis administratifs</h3>
      </div>
      <div className="mb-5 rounded-2xl border border-emerald-300/25 bg-emerald-500/10 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-200" />
          <p className="text-sm leading-relaxed text-emerald-50/90">
            La preuve de diffusion du 2026-08-25 est complète: confirmation vocale, groupe GBZ 58 employés, copie d’envoi, copie reçue et pièces jointes vérifiées.
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {followUps.map((item) => (
          <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-slate-900/70 p-5 text-sm leading-relaxed text-slate-200">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function PolitiqueCartesCreditComptesDepensesBZModal({
  isOpen,
  onClose,
}: PolitiqueCartesCreditComptesDepensesBZModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('synthese');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-amber-300/30 bg-slate-950 text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-white/10 bg-slate-950/95 px-8 py-6 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-400/10 px-4 py-2 text-sm font-semibold text-amber-100">
                    <CreditCard className="h-4 w-4" />
                    Administration & RH · Gouvernance · En vigueur
                  </div>
                  <h2 className="text-3xl font-black tracking-tight md:text-4xl">
                    Cartes de crédit et comptes de dépenses BZ
                  </h2>
                  <p className="mt-3 max-w-4xl text-base leading-relaxed text-slate-300">
                    Carte PRIsME pour cadrer la politique simplifiée V14 sur les cartes de crédit d’entreprise, les
                    comptes de dépenses, les factures, les codes de grand livre et les responsabilités de validation.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full border border-white/10 bg-white/10 p-3 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = tab.id === activeTab;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                        isActive
                          ? 'border-amber-300 bg-amber-300 text-slate-950'
                          : 'border-white/10 bg-white/[0.04] text-slate-300 hover:border-amber-300/40 hover:bg-amber-300/10'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="px-8 py-8">
              {activeTab === 'synthese' && <SyntheseTab />}
              {activeTab === 'politique' && <PolitiqueTab />}
              {activeTab === 'gouvernance' && <GouvernanceTab />}
              {activeTab === 'suivis' && <SuivisTab />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
